import React, { useEffect, useState } from "react";
import { IS } from "../../../store/slices/userInfoSlice";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import moment from "moment";
import AppLoader from "../../../components/atoms/app-loader/appLoader";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { useLazyGetQuestionPaperByIdQuery } from "../../../pages/assessment-library/slices/questionPaperAuthSlice";
import {
  useLazyGetCodingQLQuery,
  useLazyGetFibQLQuery,
  useLazyGetMcqQLQuery,
  useLazyGetSubjectiveQLQuery
} from "../../../pages/question-authoring/api/questionsApi";
import { useLazyGetScheduleDetailsQuery } from "../../../pages/assessment-library/slices/scheduleApiSlice";
import UserRole from "@lxp-app/utils/roles";

const AssessmentAuthGuard = () => {
  const navigate = useNavigate();
  const user: IS = useSelector((state: RootState) => state.UserInfoSlice);

  const newGuardOn = location?.pathname.split("/")[2];
  const pageName = location?.pathname.split("/")[3];
  const params = useParams();

  const [hasAccess, setHasAccess] = useState<boolean | null>(null);

  const [getQpDetails] = useLazyGetQuestionPaperByIdQuery();
  const [getScheduleDetails] = useLazyGetScheduleDetailsQuery();
  const [getMcqQuestionById] = useLazyGetMcqQLQuery();
  const [getFibQuestionById] = useLazyGetFibQLQuery();
  const [getCodingQuestionById] = useLazyGetCodingQLQuery();
  const [getSubjectiveQuestionById] = useLazyGetSubjectiveQLQuery();
  const accessCheck = user?.profile?.role.includes(UserRole.ASSESSMENT_CREATOR);
  const assessmentAdmin = user?.profile?.role.includes(
    UserRole.ASSESSMENT_ADMIN
  );

  const qpApiCall = async (field: string) => {
    const res: any = await getQpDetails({
      id: params?.id,
      version: params?.version
    });
    if (res?.data?.getQuestionPaperDetailsQL?.data) {
      const qpData = res?.data?.getQuestionPaperDetailsQL?.data;
      if (field == "create") {
        if (qpData?.stakeholder?.owner.includes(user?.profile?.id))
          setHasAccess(true);
        else setHasAccess(false);
      } else {
        if (qpData?.status == "inreview") {
          if (
            qpData?.stakeholder?.owner.includes(user?.profile?.id) ||
            qpData?.stakeholder?.reviewer.includes(user?.profile?.id)
          )
            setHasAccess(true);
          else setHasAccess(false);
        } else if (qpData?.status == "published") {
          setHasAccess(true);
          //  if(assessmentAdmin) setHasAccess(true);
          //  if(accessCheck && qpData?.stakeholder?.owner.includes(user?.profile?.id))
          //   setHasAccess(true);
          // else setHasAccess(false);
        } else {
          if (qpData?.stakeholder?.owner.includes(user?.profile?.id))
            setHasAccess(true);
          else setHasAccess(false);
        }
      }
    }
  };

  const questionApiCall = async (subType: string, field: string) => {
    let questionData: any = {};
    if (subType === "mcq" || subType === "scq" || subType === "tf") {
      const res: any = await getMcqQuestionById({
        questionId: params?.id,
        version: 1
      });

      if (res?.data?.getMcqQL?.data) {
        questionData = res?.data?.getMcqQL?.data;
      } else setHasAccess(false);
    } else if (subType === "coding") {
      const res: any = await getCodingQuestionById({
        questionId: params?.id,
        version: 1
      });

      if (res?.data?.getCodingQL?.data) {
        questionData = res?.data?.getCodingQL?.data;
      } else setHasAccess(false);
    } else if (subType === "video" || subType === "text") {
      const res: any = getSubjectiveQuestionById({
        questionId: params?.id,
        version: 1
      })
        .unwrap()
        .then((res) => {
          if (res?.getSubjectiveQn?.data) {
            questionData = res?.getSubjectiveQn?.data;
          } else setHasAccess(false);
        });
    } else {
      const res: any = await getFibQuestionById({
        questionId: params?.id,
        version: 1
      });
      if (res?.data?.getFibQL?.data) {
        questionData = res?.data?.getFibQL?.data;
      } else setHasAccess(false);
    }

    if (field == "create") {
      if (questionData?.stakeholder?.owner.includes(user?.profile?.id)) {
        setHasAccess(true);
      } else setHasAccess(false);
    }
  };

  const scheduleApiCall = async () => {
    try {
      const res: any = await getScheduleDetails(params?.id);
      const schedule = res?.data?.data;
      const stakeholder = schedule?.stakeHolder ?? schedule?.stakeholder;
      const userId = user?.profile?.id;
      const isOwner = stakeholder?.owner?.includes(userId);
      const isObserver = stakeholder?.observer?.includes(userId);
      const observerAccessiblePages = new Set([
        "preview-schedule",
        "invite-participants",
        "assessment-report"
      ]);
      const hasScheduleAccess =
        isOwner || (observerAccessiblePages.has(pageName) && isObserver);

      if (pageName === "invite-participants") {
        const isExpired = moment(schedule?.endTime).isBefore(moment());

        const errorKey = res?.data?.error?.[0]?.replace(/_error$/, "");

        if (errorKey === "107-102-10102010") {
          setHasAccess(false);
        } else if (isExpired) {
          setHasAccess(false);
          navigate("error/page-not-found", { replace: true });
        } else if (hasScheduleAccess) {
          setHasAccess(true);
        } else {
          setHasAccess(false);
        }
      } else {
        setHasAccess(hasScheduleAccess ? true : false);
      }
    } catch (error: any) {
      setHasAccess(false);
      console.log("error", error);
    }
  };

  useEffect(() => {
    switch (newGuardOn) {
      case "question-paper-authoring":
        if (!accessCheck) setHasAccess(false);
        else {
          switch (pageName) {
            case "create-qp":
              if (params?.id && params?.version) qpApiCall("create");
              break;
            case "preview-qp":
              if (params?.id && params?.version) qpApiCall("review");
              break;
            case "review-qp":
              if (params?.id && params?.version) qpApiCall("review");
              break;
            case "question-authoring":
              if (params?.id && params?.subType)
                questionApiCall(params?.subType, "create");
              break;
          }
          setHasAccess(true);
        }
        break;
      case "assessment-authoring": {
        const isRecruiter = user?.profile?.role?.includes(UserRole.RECRUITER);
        const scheduleStakeholderPages = [
          "preview-schedule",
          "invite-participants",
          "assessment-report"
        ] as const;
        const isScheduleStakeholderPage = scheduleStakeholderPages.includes(
          pageName as (typeof scheduleStakeholderPages)[number]
        );
        if (assessmentAdmin) {
          if (params?.id) scheduleApiCall();
        } else if (isRecruiter && isScheduleStakeholderPage && params?.id) {
          scheduleApiCall();
        } else {
          setHasAccess(false);
        }
        break;
      }
    }
  }, [newGuardOn]);

  useEffect(() => {
    if (hasAccess === false) {
      navigate("error/forbidden", { replace: true });
    }
  }, [hasAccess, navigate]);

  if (hasAccess === null) {
    return <AppLoader />;
  }

  if (hasAccess === true) {
    return <Outlet />;
  }

  return null;
};

export default AssessmentAuthGuard;
